import { useEffect, useMemo, useRef, useState, type KeyboardEvent } from 'react'
import { Grid3x3, Hash, MessageSquare, Phone, X } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import { cn } from '@/lib/utils'
import { useDialer } from '@/hooks/useDialer'
import { isDeviceReady } from '@/lib/twilioDeviceSingleton'
import { formatPhoneDisplay, normalizeToE164, validatePhoneNumber } from '@/lib/phoneValidation'
import { ouvrirConversation } from '@/lib/ouvrirConversation'
import { requeteSuggestions, useSuggestionsContacts } from '@/api/contacts'
import { useInbox } from '@/api/inbox'
import { useDebounced } from '@/hooks/useDebounced'
import { DialPad } from './DialPad'
import { CallerIdSelector } from './CallerIdSelector'

interface Props {
  darkMode?: boolean
  /** Numéro à préremplir (click-to-call). */
  initialNumber?: string
  /** Appelle tout de suite `initialNumber` dès que le téléphone est prêt. */
  autoCall?: boolean
  /** Ferme le composeur (après « Texto », le fil s'ouvre à sa place). */
  onFerme?: () => void
}

/** Une personne proposée : contact, conversation récente ou numéro tapé (nom vide). */
interface Option {
  phone: string
  nom: string
}

/** Sur écran tactile ou étroit, le pavé reste le geste naturel : ouvert d'emblée. */
const paveParDefaut = () =>
  typeof window !== 'undefined' &&
  !!(window.matchMedia?.('(max-width: 767px)').matches || window.matchMedia?.('(pointer: coarse)').matches)

/** Comparaison sans casse ni accents. */
const plat = (s: string) => s.normalize('NFD').replace(/\p{Diacritic}/gu, '').toLowerCase()

const initiales = (nom: string) =>
  nom.split(/\s+/).filter(Boolean).slice(0, 2).map((m) => m[0]?.toUpperCase()).join('')

/** Un texto part vers un numéro complet (E.164) ; un appel accepte aussi le 911. */
const textoPossible = (phone: string) => phone.startsWith('+')

/**
 * Composeur, à plat : un titre, la ligne d'où l'on part, un grand champ
 * « Nom ou numéro » (on tape, on colle, on cherche un contact), puis les
 * personnes proposées avec Appeler et Texto sur chacune. Entrée appelle,
 * Maj+Entrée écrit. Le pavé est replié sur ordinateur. Sert au bouton
 * « Composer », au click-to-call et à l'onglet Appels.
 */
export function DialerPane({ darkMode = false, initialNumber = '', autoCall = false, onFerme }: Props) {
  const { t } = useTranslation()
  const {
    availableCallerIds,
    selectedCallerId,
    handleSelectCallerId,
    placeCall,
    setComposedNumber,
    autoResolvedNumber,
    autoResolvedSource,
  } = useDialer()

  const [saisie, setSaisie] = useState(initialNumber)
  // Ligne surlignée ; -1 : aucune (champ vide, un Entrée réflexe n'appelle personne).
  const [actif, setActif] = useState(initialNumber ? 0 : -1)
  const [erreur, setErreur] = useState<string | null>(null)
  const [paveOuvert, setPaveOuvert] = useState(paveParDefaut)
  // Sur ordinateur on tape tout de suite ; sur écran tactile, pas de clavier système qui surgit.
  const [focusAuto] = useState(() => !paveParDefaut())
  const champ = useRef<HTMLInputElement>(null)

  // Click-to-call vers un autre numéro alors que le composeur est déjà ouvert.
  useEffect(() => {
    if (initialNumber) setSaisie(initialNumber)
  }, [initialNumber])

  // Référence stable vers la dernière version de placeCall (l'intervalle
  // ci-dessous ne doit pas repartir à chaque rendu).
  const placeCallRef = useRef(placeCall)
  placeCallRef.current = placeCall
  const autoCallFiredFor = useRef<string | null>(null)

  // Auto-déclenchement de l'appel quand auto=1 (poll Device 12 s max).
  useEffect(() => {
    if (!autoCall || !initialNumber) return
    if (autoCallFiredFor.current === initialNumber) return // déjà déclenché pour ce numéro
    if (availableCallerIds.length === 0) return

    let attempts = 0
    const maxAttempts = 24 // 24 × 500ms = 12s
    const interval = setInterval(() => {
      attempts++
      if (isDeviceReady()) {
        clearInterval(interval)
        autoCallFiredFor.current = initialNumber
        placeCallRef.current(initialNumber)
      } else if (attempts >= maxAttempts) {
        clearInterval(interval)
        autoCallFiredFor.current = initialNumber
        toast.info(t('phone.clickToCall.deviceNotReady', 'Téléphone en cours de connexion'), {
          description: t(
            'phone.clickToCall.tapToCall',
            'Appuyez sur le bouton vert pour démarrer l\'appel.',
          ),
        })
      }
    }, 500)

    return () => clearInterval(interval)
  }, [autoCall, initialNumber, availableCallerIds.length, t])

  const texte = saisie.trim()
  const estNumero = /^[\d\s()+.\-*#]+$/.test(texte)

  // Récents : les dernières conversations, même requête que la liste à l'ouverture (déjà en cache).
  const recents = useInbox('all', '', null)
  // Une recherche par pause dans la frappe, pas une par touche.
  const suggestions = useSuggestionsContacts(useDebounced(texte, 200))

  const options = useMemo<Option[]>(() => {
    if (!texte) {
      const vus = new Set<string>()
      const liste: Option[] = []
      for (const item of recents.data?.pages[0]?.items ?? []) {
        if (vus.has(item.phone)) continue
        vus.add(item.phone)
        liste.push({ phone: item.phone, nom: item.name })
        if (liste.length === 5) break
      }
      return liste
    }
    const mots = plat(texte).split(/\s+/)
    const contacts = (suggestions.data ?? [])
      .filter((c) =>
        estNumero ||
        mots.every((m) => plat(`${c.first_name ?? ''} ${c.last_name ?? ''} ${c.company_name ?? ''} ${c.email ?? ''}`).includes(m)),
      )
      .map((c) => ({
        phone: c.phone_number,
        nom: c.full_name || `${c.first_name ?? ''} ${c.last_name ?? ''}`.trim(),
      }))
    // Le numéro tapé, s'il est complet et n'est pas déjà celui d'un contact proposé.
    if (estNumero && validatePhoneNumber(texte).isValid) {
      const tape = normalizeToE164(texte) ?? texte.replace(/[\s()-]/g, '')
      if (!contacts.some((c) => c.phone === tape)) return [{ phone: tape, nom: '' }, ...contacts]
    }
    return contacts
  }, [texte, estNumero, recents.data, suggestions.data])

  // Dès qu'on tape, la meilleure suggestion est surlignée : Entrée l'appelle.
  useEffect(() => setActif(texte ? 0 : -1), [texte])
  const surligne = actif >= 0 ? (options[actif] ?? null) : null

  // Aperçu du numéro présenté en mode Automatique.
  const numeroVise = surligne?.phone ?? (estNumero ? texte : '')
  useEffect(() => setComposedNumber(numeroVise), [numeroVise, setComposedNumber])

  const modifier = (valeur: string) => {
    setSaisie(valeur)
    setErreur(null)
  }

  const appeler = (o: Option | null) => {
    const numero = o?.phone ?? (estNumero ? texte : '')
    if (!numero || !validatePhoneNumber(numero).isValid) {
      setErreur(t('phone.invalidNumber', 'Numéro invalide'))
      return
    }
    placeCall(numero)
    modifier('')
  }

  const ecrire = (o: Option | null) => {
    const numero = o?.phone ?? (estNumero ? normalizeToE164(texte) : null)
    if (!numero || !textoPossible(numero)) {
      setErreur(t('phone.invalidNumber', 'Numéro invalide'))
      return
    }
    ouvrirConversation(numero)
    onFerme?.()
  }

  const auClavier = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'ArrowDown' && options.length) {
      e.preventDefault()
      setActif((i) => Math.min(i + 1, options.length - 1))
    } else if (e.key === 'ArrowUp' && options.length) {
      e.preventDefault()
      setActif((i) => Math.max(i - 1, 0))
    } else if (e.key === 'Enter') {
      e.preventDefault()
      if (!surligne && !texte) return
      if (e.shiftKey) ecrire(surligne)
      else appeler(surligne)
    } else if (e.key === 'Escape' && saisie) {
      e.preventDefault()
      modifier('')
    }
  }

  const sansResultat =
    !!texte && !estNumero && requeteSuggestions(texte).length >= 2 && suggestions.isSuccess && !suggestions.isFetching && options.length === 0

  const ligneUnique = availableCallerIds.length === 1 ? availableCallerIds[0] : null
  const sourdine = darkMode ? 'text-slate-400' : 'text-slate-500'
  const discret = darkMode ? 'text-slate-500' : 'text-slate-400'
  const touche = cn(
    'inline-flex min-w-[1.25rem] items-center justify-center rounded border px-1 font-sans text-[10px] leading-4',
    darkMode ? 'border-slate-700 text-slate-400' : 'border-slate-300 text-slate-500',
  )
  const actionLigne = cn(
    'inline-flex h-7 items-center gap-1.5 rounded-md px-2 text-xs font-semibold transition-colors',
    'focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-1',
    darkMode ? 'focus-visible:outline-indigo-400' : 'focus-visible:outline-slate-900',
  )

  return (
    <div className="flex-1 overflow-y-auto">
      <div className="mx-auto w-full max-w-xl px-6 pt-10 pb-8">
        <h2 className={cn('text-xl font-semibold tracking-tight', darkMode ? 'text-white' : 'text-slate-900')}>
          {t('phone.composer.title', 'Appeler ou écrire')}
        </h2>
        {(ligneUnique || availableCallerIds.length > 1) && (
          <div className={cn('mt-2 flex flex-wrap items-center gap-2 text-sm', sourdine)}>
            <span>{t('phone.composer.from', 'Depuis')}</span>
            {ligneUnique ? (
              <span className={cn('rounded-md px-2 py-1 font-medium', darkMode ? 'bg-slate-800 text-slate-200' : 'bg-slate-100 text-slate-700')}>
                {/* Le nom donné par le client, sinon le numéro lui-même (comme le sélecteur). */}
                {ligneUnique.label || formatPhoneDisplay(ligneUnique.phone_number)}
              </span>
            ) : (
              <CallerIdSelector
                inline
                numbers={availableCallerIds}
                selectedCallerId={selectedCallerId}
                onSelect={handleSelectCallerId}
                darkMode={darkMode}
                autoResolvedNumber={autoResolvedNumber}
                autoResolvedFromSticky={autoResolvedSource === 'sticky'}
              />
            )}
          </div>
        )}

        {/* Le champ, à plat sur un trait fin : c'est lui le sujet de l'écran. */}
        <div
          className={cn(
            'mt-6 flex items-center gap-2 border-b pb-2 transition-colors',
            darkMode ? 'border-slate-700 focus-within:border-indigo-400' : 'border-slate-300 focus-within:border-slate-900',
          )}
        >
          <input
            ref={champ}
            autoFocus={focusAuto}
            type="text"
            autoComplete="off"
            role="combobox"
            aria-expanded={options.length > 0}
            aria-controls="composeur-options"
            aria-activedescendant={surligne ? `composeur-option-${actif}` : undefined}
            value={saisie}
            onChange={(e) => modifier(e.target.value)}
            onKeyDown={auClavier}
            placeholder={t('phone.composer.placeholder', 'Nom ou numéro')}
            className={cn(
              'min-w-0 flex-1 bg-transparent text-2xl font-light tracking-tight outline-none',
              darkMode ? 'text-white placeholder-slate-600' : 'text-slate-900 placeholder-slate-400',
            )}
          />
          {saisie && (
            <button
              type="button"
              onClick={() => {
                modifier('')
                champ.current?.focus()
              }}
              title={t('phone.composer.clear', 'Effacer')}
              className={cn(
                'rounded-md p-1.5 transition-colors',
                darkMode ? 'text-slate-500 hover:bg-slate-800 hover:text-white' : 'text-slate-400 hover:bg-slate-100 hover:text-slate-700',
              )}
            >
              <X className="h-4 w-4" />
            </button>
          )}
        </div>
        {/* Hauteur réservée : l'erreur n'y décale rien (le pavé reste sous le doigt). */}
        <p aria-live="polite" className={cn('mt-1.5 h-4 text-xs font-medium', darkMode ? 'text-red-400' : 'text-red-600')}>
          {erreur ?? ''}
        </p>

        {/* Le pavé juste sous le champ : les suggestions changent en dessous sans le déplacer. */}
        {paveOuvert && (
          <div className="mx-auto mt-2 mb-4 max-w-xs">
            <DialPad darkMode={darkMode} onDigit={(d) => modifier(saisie + d)} />
          </div>
        )}

        {options.length > 0 && (
          <div className="mt-2">
            <p className={cn('pb-1.5 text-[10.5px] font-semibold uppercase tracking-wider', discret)}>
              {texte ? t('phone.composer.suggestions', 'Suggestions') : t('phone.composer.recents', 'Récents')}
            </p>
            <div id="composeur-options" role="listbox" className="-mx-3 space-y-px">
              {options.map((o, i) => {
                const estActif = i === actif
                return (
                  <div
                    key={`${o.phone}-${i}`}
                    id={`composeur-option-${i}`}
                    role="option"
                    aria-selected={estActif}
                    onMouseEnter={() => setActif(i)}
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => setActif(i)}
                    className={cn(
                      'group flex cursor-default items-center gap-3 rounded-lg px-3 py-2 transition-colors',
                      estActif ? (darkMode ? 'bg-slate-800' : 'bg-slate-100') : '',
                    )}
                  >
                    <span
                      className={cn(
                        'flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-[11px] font-bold',
                        darkMode ? 'bg-slate-700 text-slate-200' : 'bg-slate-200 text-slate-700',
                      )}
                    >
                      {o.nom ? initiales(o.nom) : <Hash className="h-3.5 w-3.5" />}
                    </span>
                    <span className="min-w-0 flex-1">
                      <span className={cn('block truncate text-sm font-medium', darkMode ? 'text-white' : 'text-slate-900')}>
                        {o.nom || formatPhoneDisplay(o.phone)}
                      </span>
                      <span className={cn('block truncate text-xs', sourdine)}>
                        {o.nom ? formatPhoneDisplay(o.phone) : t('phone.composer.typed', 'Numéro saisi')}
                      </span>
                    </span>
                    {/* Les deux gestes, sur la personne : visibles sur la ligne surlignée ou survolée. */}
                    <span className={cn('flex shrink-0 items-center gap-1 transition-opacity', estActif ? 'opacity-100' : 'opacity-0 group-hover:opacity-100 focus-within:opacity-100')}>
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation()
                          appeler(o)
                        }}
                        className={cn(actionLigne, 'bg-green-500 text-white hover:bg-green-600')}
                      >
                        <Phone className="h-3.5 w-3.5" />
                        {t('phone.call', 'Appeler')}
                      </button>
                      {textoPossible(o.phone) && (
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation()
                            ecrire(o)
                          }}
                          className={cn(
                            actionLigne,
                            darkMode ? 'text-slate-200 hover:bg-slate-700' : 'text-slate-700 hover:bg-slate-200',
                          )}
                        >
                          <MessageSquare className="h-3.5 w-3.5" />
                          {t('phone.composer.textShort', 'Texto')}
                        </button>
                      )}
                    </span>
                  </div>
                )
              })}
            </div>
          </div>
        )}

        {sansResultat && (
          <p className={cn('mt-2 text-sm', sourdine)}>
            {t('phone.composer.noMatch', 'Aucun contact pour « {{q}} »', { q: texte })}
          </p>
        )}
        {!texte && options.length === 0 && recents.isSuccess && (
          <p className={cn('mt-2 text-sm', sourdine)}>{t('phone.composer.hint', 'Tapez un nom ou collez un numéro.')}</p>
        )}

        {/* Les raccourcis, et le pavé pour qui le veut. */}
        <div className={cn('mt-6 flex flex-wrap items-center justify-between gap-3 text-xs', discret)}>
          <span className="hidden items-center gap-3 md:flex">
            <span className="flex items-center gap-1.5">
              <kbd className={touche}>↵</kbd>
              {t('phone.call', 'Appeler')}
            </span>
            <span className="flex items-center gap-1.5">
              <kbd className={touche}>⇧</kbd>
              <kbd className={touche}>↵</kbd>
              {t('phone.composer.text', 'Écrire un texto')}
            </span>
          </span>
          <button
            type="button"
            onClick={() => {
              setPaveOuvert((v) => !v)
              // Sur ordinateur, on peut continuer de taper au clavier.
              if (focusAuto) champ.current?.focus()
            }}
            aria-expanded={paveOuvert}
            className={cn(
              'flex items-center gap-1.5 rounded-md px-2 py-1 font-medium transition-colors',
              darkMode ? 'hover:bg-slate-800 hover:text-white' : 'hover:bg-slate-100 hover:text-slate-900',
            )}
          >
            <Grid3x3 className="h-3.5 w-3.5" />
            {paveOuvert ? t('phone.composer.hideKeypad', 'Masquer le clavier') : t('phone.keypad', 'Clavier')}
          </button>
        </div>
      </div>
    </div>
  )
}
